import { classes } from 'html-classes';
import { useTranslation } from 'react-i18next';

import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import { defaultSrcSetParams } from '~/components/CloudflareResponsiveImage/constants';
import type { CartItem } from '~/stores/CartStore';
import type {
  CalculateStockBundleError,
  CalculateStockOfferError,
} from '~/stores/shared/CartStockChanges/interfaces';
import { isProductBundle } from '~/types/Product';
import { capByOrderLimit } from '~/utils/orderLimit';

import styles from './StockProductCard.module.scss';

interface StockProductCardProps {
  product: CartItem;
  stockInfo: CalculateStockOfferError;
  bundleStockInfo?: Nullable<CalculateStockBundleError>;
  className?: string;
}

const StockProductCard = ({
  product,
  stockInfo,
  bundleStockInfo,
  className,
}: StockProductCardProps) => {
  const { t } = useTranslation();
  const isBundle = isProductBundle(product);

  const getProductStock = () => {
    if (isBundle) {
      if (!bundleStockInfo) {
        return 0;
      }

      return bundleStockInfo.sellable >= 0 ? bundleStockInfo?.sellable : 0;
    }

    return capByOrderLimit(
      Math.max(stockInfo.on_stock, 0),
      stockInfo.max_quantity_per_order,
    );
  };

  const onStock = getProductStock();

  return (
    <div className={classes([styles.root, className])}>
      <div className={styles.preview}>
        <CloudflareResponseImage
          src={product.previewImageThumb}
          srcParams={{ width: 96, height: 96 }}
          srcSetParams={defaultSrcSetParams}
          alt={product.name}
          className={styles.image}
          loading="lazy"
        />
      </div>
      <h2 className={styles.title}>{product.name}</h2>
      <span
        className={classes([
          styles.available,
          onStock <= 0 && styles.outOfStock,
        ])}
      >
        {onStock > 0
          ? t('phrases:availableStock', { count: onStock })
          : t('outStock')}
      </span>
    </div>
  );
};

export default StockProductCard;
